import {useMutation} from '@apollo/client';
import {ResetTransactionDocument} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {Modal} from 'src/Modal';
import type {SystemProps} from 'src/Modals';

export type ResetTransactionModalUserProps = {
  transactionId: string;
  onReset?: () => void;
};

export const ResetTransactionModal = (
  props: ResetTransactionModalUserProps & SystemProps,
) => {
  const {transactionId, onReset, onRequestClose} = props;
  const [resetTransaction, {loading}] = useMutation(ResetTransactionDocument);
  const [error, setError] = React.useState<string | null>(null);

  const handleReset = async () => {
    setError(null);
    try {
      await resetTransaction({variables: {id: transactionId}});
      onReset?.();
      onRequestClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to reset transaction');
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="destructive"
        onClick={() => void handleReset()}
        disabled={loading}
      >
        {loading ? 'Resetting...' : 'Reset'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Reset transaction"
      footer={footer}
      size="sm"
    >
      {error && (
        <div className="bg-destructive/10 text-destructive mb-3 rounded-md p-3 text-sm">
          {error}
        </div>
      )}
      <p>
        Are you sure you want to reset this transaction? It will be deleted and
        its raw transactions returned to the inbox.
      </p>
    </Modal>
  );
};
